import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { postData } from '../../../helpers/apiUtils';
import { HubBroker, HubSite } from '../types';
import { SNIPPET_GLOBAL_SITE_ID, SnippetListRow } from './types';

interface Props {
  open: boolean;
  sites: HubSite[];
  brokers: HubBroker[];
  /** Bestehende Zeilen — für die Vorab-Warnung, dass ein Name auf dieser Ebene schon existiert. */
  vorhanden: SnippetListRow[];
  onClose: () => void;
  onCreated: (row: SnippetListRow) => void;
}

/** Erlaubte Form des Namens — dieselbe Regel wie `assertGueltigerSlug` im Service. */
const SLUG_MUSTER = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/;

/**
 * Einen Schnipsel anlegen: Name, EBENE, Broker-Bezug, Titel. Der Inhalt kommt danach im Editor.
 *
 * ⚠ Die Ebene ist die folgenreichste Angabe auf diesem Formular und lässt sich später nicht
 * ändern (`(slug, site_id)` ist der Schlüssel). Deshalb steht sie oben, nicht unten, und der
 * Dialog sagt in Worten, was sie bedeutet — „site_id" ist für niemanden ausser uns eine Auskunft.
 */
const NewSnippetDialog: React.FC<Props> = ({
  open,
  sites,
  brokers,
  vorhanden,
  onClose,
  onCreated,
}) => {
  const [slug, setSlug] = useState('');
  const [siteId, setSiteId] = useState<number>(SNIPPET_GLOBAL_SITE_ID);
  const [brokerId, setBrokerId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSlug('');
    setSiteId(SNIPPET_GLOBAL_SITE_ID);
    setBrokerId('');
    setTitle('');
    setError(null);
    setBusy(false);
  }, [open]);

  const nameOk = SLUG_MUSTER.test(slug.trim());

  // Was auf DIESER Ebene mit diesem Namen schon existiert — das lehnt der Server ohnehin ab, aber
  // erst nach dem Absenden. Hier steht es, bevor jemand tippt.
  const kollision = useMemo(
    () => vorhanden.find((r) => r.slug === slug.trim() && Number(r.site_id) === Number(siteId)),
    [vorhanden, slug, siteId],
  );
  // Und was auf einer ANDEREN Ebene existiert — keine Kollision, aber eine Überdeckung, die man
  // kennen muss: eine neue Portal-Fassung verdrängt dort die globale.
  const andereEbenen = useMemo(
    () => vorhanden.filter((r) => r.slug === slug.trim() && Number(r.site_id) !== Number(siteId)),
    [vorhanden, slug, siteId],
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    const res = await postData<SnippetListRow>('/api/broker-hub/snippets', {
      slug: slug.trim(),
      site_id: Number(siteId),
      broker_id: brokerId === '' ? null : Number(brokerId),
      title: title.trim() || null,
      content_blocks: [],
    });
    setBusy(false);
    if (!res.success || !res.data) {
      setError(res.error || 'Der Schnipsel konnte nicht angelegt werden.');
      return;
    }
    onCreated(res.data);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Neuen Schnipsel anlegen</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ein Schnipsel ist ein wiederverwendbarer Baustein-Block. Im Artikel steht er später als{' '}
          <code>{'[bh name="…"]'}</code>.
        </Typography>

        <TextField
          label="Ebene"
          select
          fullWidth
          margin="dense"
          value={siteId}
          onChange={(e) => setSiteId(Number(e.target.value))}
          helperText={
            Number(siteId) === SNIPPET_GLOBAL_SITE_ID
              ? 'Gilt auf allen Portalen — ausser dort, wo es eine eigene Fassung gibt.'
              : 'Gilt nur auf diesem Portal und verdrängt dort die globale Fassung.'
          }
        >
          <MenuItem value={SNIPPET_GLOBAL_SITE_ID}>Global (alle Portale)</MenuItem>
          {sites.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              Nur {s.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Name im Artikel"
          fullWidth
          margin="dense"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          error={slug.length > 0 && !nameOk}
          helperText={
            slug.length > 0 && !nameOk
              ? 'Erlaubt sind Kleinbuchstaben, Ziffern und Bindestriche (2–64 Zeichen).'
              : 'Kleinbuchstaben, Ziffern, Bindestriche. Beispiel: broker-vergleich-cfd'
          }
        />

        <TextField
          label="Broker-Bezug"
          select
          fullWidth
          margin="dense"
          value={brokerId}
          onChange={(e) => setBrokerId(e.target.value === '' ? '' : Number(e.target.value))}
          helperText="Nötig, sobald der Schnipsel einen Affiliate-Link oder einen Broker-Fakt enthält — daran hängen Pflicht-Stern, Risikohinweis und rel=sponsored."
        >
          <MenuItem value="">
            <em>kein Broker-Bezug</em>
          </MenuItem>
          {brokers.map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Titel (nur intern)"
          fullWidth
          margin="dense"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          helperText="Steht nur in der Übersicht, nie auf der Seite."
        />

        {kollision && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Diesen Namen gibt es auf dieser Ebene schon
            {kollision.title ? ` („${kollision.title}“)` : ''}. Bearbeite die vorhandene Fassung,
            statt eine zweite anzulegen.
          </Alert>
        )}
        {!kollision && andereEbenen.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Diesen Namen gibt es bereits auf{' '}
            {andereEbenen
              .map((r) =>
                Number(r.site_id) === SNIPPET_GLOBAL_SITE_ID
                  ? 'globaler Ebene'
                  : `Portal-Ebene (${r.site_name ?? r.site_key})`,
              )
              .join(', ')}
            .{' '}
            {Number(siteId) === SNIPPET_GLOBAL_SITE_ID
              ? 'Die Portal-Fassung bleibt dort vorrangig — deine globale wirkt auf den übrigen Portalen.'
              : 'Deine neue Fassung verdrängt die globale auf genau diesem Portal.'}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box flexGrow={1} />
        <Button onClick={onClose} disabled={busy}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !nameOk || Boolean(kollision)}
        >
          Anlegen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewSnippetDialog;
